/**
 * Research Tasks (platform-neutral, no JSX): what a compile or refine sends,
 * and the rows the plan preview shows for a research plan. Both builders and
 * both composers read these.
 *
 * The Research, Image and Result controls are the chat composer's controls
 * (WP3) with the same availability rules, but a task keeps its own picks: a
 * new task starts at Auto, an existing one shows what its plan saved, and the
 * chat's sticky preference is never read or written here. They are sent in
 * the same `capabilities` shape the chat request uses; the server resolves
 * them again and is the final word on the saved plan.
 */
import type {
  AgentChatCapabilities,
  NovaAutomation,
  NovaReportPreview,
  NovaReportSourceScope,
} from '@tabbio/types';
import {
  readKnownAgentCapabilities,
  resolveAgentCapabilitiesRequest,
  type AgentCapabilitiesPreference,
} from '../../lib/agent-capabilities-preference';
import { resolveDeviceTimezone } from '../../lib/device-timezone';

/** A new task starts at Auto for Research, Image and Result. */
export function novaDefaultCapabilities(): AgentCapabilitiesPreference {
  return { research: 'auto', image: 'auto', artifact: 'auto' };
}

/**
 * The picks a saved plan carries (its `report` output decision), for the
 * controls of an existing task. A job scan has none: Auto.
 */
export function capabilitiesFromPlan(
  dsl: Pick<NovaAutomation, 'outputs'> | null | undefined,
): AgentCapabilitiesPreference {
  const output = dsl?.outputs?.find((item) => item.type === 'report');
  const params = (output?.params ?? {}) as Record<string, unknown>;
  const toggle = (value: unknown) =>
    value === 'on' || value === 'off' ? value : 'auto';
  const format = params.format;
  return {
    research: toggle(params.research),
    image: toggle(params.image),
    artifact:
      format === 'summary' || format === 'document' || format === 'slides'
        ? format
        : 'auto',
  };
}

/**
 * The controls and timezone a Task compile or refine carries: the task's own
 * picks, reconciled with what the account may use (never the chat's sticky
 * preference).
 */
export function novaCompileContext(
  capabilities: AgentCapabilitiesPreference = novaDefaultCapabilities(),
): {
  capabilities: Required<AgentChatCapabilities>;
  timezone?: string;
} {
  return {
    capabilities: resolveAgentCapabilitiesRequest(
      capabilities,
      readKnownAgentCapabilities(),
    ),
    timezone: resolveDeviceTimezone(),
  };
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type NovaReportPreviewRowId =
  | 'sources'
  | 'result'
  | 'image'
  | 'delivery'
  | 'credits';

export interface NovaReportPreviewRow {
  id: NovaReportPreviewRowId;
  label: string;
  values: string[];
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function repoOf(url: string): string {
  try {
    const [owner, repo] = new URL(url).pathname.split('/').filter(Boolean);
    return owner && repo ? `${owner}/${repo}` : url;
  } catch {
    return url;
  }
}

function sourceLine(t: Translate, scope: NovaReportSourceScope): string {
  switch (scope.kind) {
    case 'web': {
      const what = scope.region
        ? t('nova.report.sources.webRegion', {
            topic: scope.topic,
            region: scope.region,
          })
        : t('nova.report.sources.web', { topic: scope.topic });
      return `${what} · ${t(`nova.report.window.${scope.timeWindow}`)}`;
    }
    case 'links':
      return t('nova.report.sources.links', {
        list: [...new Set(scope.urls.map(hostOf))].join(
          t('nova.report.separator'),
        ),
      });
    case 'pdf':
      return t('nova.report.sources.pdf', { number: scope.count });
    case 'github':
      return t('nova.report.sources.github', { repo: repoOf(scope.repo) });
    case 'skill':
      return t('nova.report.sources.skill');
  }
}

/**
 * The research preview as label and value rows: what it reads (sources
 * scope), what it makes (format and files), the image, where it goes, and the
 * estimated credits of one run.
 */
export function reportPreviewRows(
  t: Translate,
  report: NovaReportPreview,
): NovaReportPreviewRow[] {
  const files = report.formats
    .filter((format) => format !== 'html')
    .map((format) => format.toUpperCase());
  const result =
    report.format === 'summary'
      ? t('nova.report.format.summary')
      : t(`nova.report.format.${report.format}`, {
          files: [...files, 'HTML'].join(t('nova.report.separator')),
        });
  return [
    {
      id: 'sources',
      label: t('nova.report.sourcesLabel'),
      values: report.sources.map((scope) => sourceLine(t, scope)),
    },
    { id: 'result', label: t('nova.report.resultLabel'), values: [result] },
    {
      id: 'image',
      label: t('nova.report.imageLabel'),
      values: [
        report.includeImage
          ? t('nova.report.image.included')
          : t('nova.report.image.notIncluded'),
      ],
    },
    {
      id: 'delivery',
      label: t('nova.report.deliveryLabel'),
      values: [
        report.channels
          .map((channel) => t(`nova.report.channels.${channel}`))
          .join(t('nova.report.separator')),
      ],
    },
    {
      id: 'credits',
      label: t('nova.report.creditsLabel'),
      values: [String(report.estimatedCredits)],
    },
  ].filter((row) => row.values.some((value) => value.trim().length > 0)) as NovaReportPreviewRow[];
}
